import React from "react";
import { useParams, Redirect } from "react-router-dom";
import tw from "twin.macro";
import {
  features,
  privateWorkspaceCompetitors,
  noPrivateWorkspaceCompetitors,
} from "./Comparison";
import { getPrimaryUrl } from "./helpers/utmHelper";

const allCompetitors = [
  ...privateWorkspaceCompetitors,
  ...noPrivateWorkspaceCompetitors,
];

const PageWrapper = tw.div`max-w-5xl mx-auto px-4 py-12`;
const HeroSection = tw.div`text-center mb-16`;
const PageTitle = tw.h1`text-3xl md:text-5xl font-bold text-secondary-500 mb-4`;
const Subtitle = tw.p`text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed`;

const Section = tw.div`mb-16`;
const SectionTitle = tw.h2`text-2xl md:text-3xl font-bold text-secondary-500 mb-6`;

const ComparisonTable = tw.table`w-full border-collapse rounded-xl overflow-hidden shadow-lg`;
const TableHeader = tw.thead`bg-gray-100`;
const TableHeaderRow = tw.tr`border-b border-gray-200`;
const TableHeaderCellFeature = tw.th`px-4 py-4 text-left font-bold text-primary-500`;
const TableHeaderCellCompetitor = tw.th`px-4 py-4 text-center font-bold text-primary-500`;
const TableHeaderCellUclusion = tw.th`px-4 py-4 text-center font-bold text-primary-500 bg-green-500`;
const TableBody = tw.tbody``;
const TableRow = tw.tr`border-b border-gray-100 transition-colors duration-150 hover:bg-gray-100`;
const TableCellFeature = tw.td`px-4 py-4 text-left text-sm font-semibold text-primary-500`;
const TableCellCompetitor = tw.td`px-4 py-4 text-center text-sm`;
const TableCellUclusion = tw.td`px-4 py-4 text-center text-sm bg-green-500`;
const CheckmarkIcon = tw.svg`w-6 h-6 mx-auto text-primary-500`;
const NoText = tw.span`text-gray-500 text-sm`;
const PartialText = tw.span`text-gray-600 text-sm italic`;

const CTASection = tw.div`text-center mt-16 py-12 bg-green-500 rounded-2xl`;
const CTATitle = tw.h2`text-2xl md:text-3xl font-bold text-secondary-500 mb-4`;
const CTASubtitle = tw.p`text-gray-600 mb-8 text-lg`;
const CTAButton = tw.a`px-10 py-4 bg-blue-500 text-white no-underline hover:bg-primary-500 font-semibold inline-block rounded-xl shadow-lg tracking-wide uppercase text-sm transition-all duration-300 ease-in-out`;

const Checkmark = () => (
  <CheckmarkIcon
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth="2.5"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4.5 12.75l6 6 9-13.5"
    />
  </CheckmarkIcon>
);

const renderCellValue = (value) => {
  if (value === true) return <Checkmark />;
  if (value === false) return <NoText>No.</NoText>;
  return <PartialText>{value}</PartialText>;
};

const VersusPage = ({ isMobile }) => {
  const { slug } = useParams();
  const competitor = allCompetitors.find((c) => c.slug === slug);

  if (!competitor) {
    return <Redirect to="/" />;
  }

  return (
    <PageWrapper style={{ marginTop: isMobile ? "4rem" : "5rem" }}>
      <HeroSection>
        <PageTitle>
          {competitor.name} alternative to Uclusion
        </PageTitle>
        <Subtitle>{competitor.description}</Subtitle>
      </HeroSection>

      <Section>
        <SectionTitle>
          {competitor.name} vs Uclusion: Feature comparison
        </SectionTitle>
        <ComparisonTable>
          <TableHeader>
            <TableHeaderRow>
              <TableHeaderCellFeature>Feature</TableHeaderCellFeature>
              <TableHeaderCellCompetitor>
                {competitor.name}
              </TableHeaderCellCompetitor>
              <TableHeaderCellUclusion>Uclusion</TableHeaderCellUclusion>
            </TableHeaderRow>
          </TableHeader>
          <TableBody>
            {features.map((feature, index) => (
              <TableRow key={index}>
                <TableCellFeature>{feature}</TableCellFeature>
                <TableCellCompetitor>
                  {renderCellValue(competitor.features[feature])}
                </TableCellCompetitor>
                <TableCellUclusion>
                  <Checkmark />
                </TableCellUclusion>
              </TableRow>
            ))}
          </TableBody>
        </ComparisonTable>
      </Section>

      <Section id="detail-section">
        <SectionTitle>Detail of how {competitor.name} falls short</SectionTitle>
        {competitor.detailPage}
      </Section>

      <CTASection>
        <CTATitle>Ready to try Uclusion?</CTATitle>
        <CTASubtitle>
          See how Uclusion compares to {competitor.name} first-hand with a
          sandbox demo.
        </CTASubtitle>
        <CTAButton href={getPrimaryUrl()}>Start free</CTAButton>
      </CTASection>
    </PageWrapper>
  );
};

export default VersusPage;
